import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import NewContactsTableRow from './NewContactsTableRow';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './newContactsTable.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import { getPerformanceNewContactsTable } from '@/store/slices/components/dashboard/service';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function NewContactsTable() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { newContacts } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const headers = [
    {
      id: 1,
      title: Trans(translations, 'Contact'),
      field: TABLE_SORT_FIELDS.CONTACT_NAME,
    },
    {
      id: 2,
      title: Trans(translations, 'Source'),
      field: TABLE_SORT_FIELDS.ORIGIN,
    },
    {
      id: 3,
      title: Trans(translations, 'ContactOperations.FollowUpDays'),
      field: TABLE_SORT_FIELDS.DAYS_OF_FOLLOW_UP,
    },
    {
      id: 4,
      title: Trans(translations, 'Agent'),
      subtitle: Trans(translations, 'Branch'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 5,
      title: Trans(translations, 'ContactOperations.Assignment'),
      subtitle: Trans(translations, 'ContactOperations.Closing'),
      field: TABLE_SORT_FIELDS.ASSIGNMENT_DATE,
    },
  ];

  useEffect(() => {
    if (!newContacts.has_next) return;
    if (!isInViewport || newContacts.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(newContacts, { withNextPage: true });

    dispatch(getPerformanceNewContactsTable({ query, tableQuery }));
  }, [isInViewport]);

  const handleCellClick = (header) => {
    dispatch(
      setPerformanceModals({
        key: 'newContacts',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.newContacts(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      className={`${parentStyles.table} ${specificStyles.newContactsTable}`}
      id="new-contacts-table"
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={newContacts.sortBy}
            sortDirection={newContacts.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {newContacts.list.map((contact) => (
          <NewContactsTableRow key={contact.id} contact={contact} />
        ))}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(newContacts.loading || newContacts.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
